<template>
  <div>
    <div class="d-flex" style="width: 520px">
      <v-text-field
        v-model="search"
        class="fix-google-chrome mr-2"
        label="รหัส - ชื่อพนักงาน"
        prepend-inner-icon="mdi-magnify"
        clearable
        outlined
        dense
        hide-details
        @input="handleSearch"
      ></v-text-field>
      <v-autocomplete
        v-model="filterOrganization"
        class="fix-google-chrome"
        label="หน่วยงาน"
        :items="organizations"
        item-text="title_th"
        item-value="organization_uuid"
        outlined
        dense
        hide-details
        clearable
        @change="handleFilterOrganization"
        :loading="loadingFilterOrganization"
      >
      </v-autocomplete>
      <v-btn class="ml-2" icon @click="handleRefresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-data-table
      class="mt-4 with-borders"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :loading="loading"
      :headers="headers"
      :items="flattenedRows"
      :options.sync="optionsEmployee"
      :server-items-length="authorityEmployeeSummary.total"
      :items-per-page="10"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100],
        itemsPerPageText: 'Rows per page:',
      }"
      must-sort
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-if="!items.length">
            <td style="text-align: center" colspan="4">No data available</td>
          </tr>
          <tr v-for="(item, index) in items" :key="index">
            <!-- Employee -->
            <td
              :style="`vertical-align: ${
                item.employeeRowSpan > 1 ? 'top' : 'middle'
              }; padding: 10px 15px`"
              v-if="item.showEmployee"
              :rowspan="item.employeeRowSpan"
            >
              {{ item.employeeName }} ({{ item.employeeCode }})<br />
              <span class="grey--text caption">{{
                item.organizationName
              }}</span>
            </td>

            <!-- Hardware -->
            <td
              :style="`vertical-align: ${
                item.hardwareRowSpan > 1 ? 'top' : 'middle'
              }; padding: 10px 15px`"
              v-if="item.showHardware"
              :rowspan="item.hardwareRowSpan"
            >
              {{ item.hardwareCode }} - {{ item.hardwareTitle }} -
              {{ item.hardwareRowSpan }}
            </td>

            <!-- Authority -->
            <td>
              <div>{{ item.authority }}</div>
              <div class="caption grey--text">
                {{ item.accessControlStartTime }} -
                {{ item.accessControlEndTime }}
              </div>
            </td>

            <!-- Timezone -->
            <td>
              <div class="d-flex justify-space-between align-center">
                <div>{{ item.timezone }}</div>
                <div>
                  <v-btn
                    text
                    color="error"
                    @click.stop="onDeleteDevice(item)"
                    :disabled="
                      uiCtrl &&
                      !uiCtrl.isEnable('btn:::authority_hardware:delete')
                    "
                    >ลบ</v-btn
                  >
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <ConfirmDialog
      :dialog="confirmDeleteDevice"
      :loading="confirmDeleteDeviceLoading"
      :dialogData="confirmDeleteDeviceData"
      @cancelConfirmDialog="cancelConfirmDeleteDevice"
      @submitConfirmDialog="submitConfirmDeleteDevice"
    />
  </div>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'

export default {
  components: {
    ConfirmDialog,
  },
  props: {
    dialogCheckPermissions: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      filterOrganization: null,
      sortBy: ['employeeCode'],
      sortDesc: [false],
      loading: false,
      loadingFilterOrganization: false,
      headers: [
        {
          text: 'พนักงาน',
          align: 'start',
          value: 'employeeCode',
          width: '25%',
        },
        {
          text: 'Device ที่พนักงานใช้งานได้',
          value: 'hardwareName',
          width: '25%',
          sortable: false,
        },
        {
          text: 'กลุ่มสิทธิ์',
          value: 'authority',
          width: '25%',
          sortable: false,
        },
        {
          text: 'ช่วงเวลา',
          value: 'timezone',
          width: '25%',
          sortable: false,
        },
      ],
      optionsEmployee: {},
      confirmDeleteDevice: false,
      confirmDeleteDeviceLoading: false,
      confirmDeleteDeviceData: {},
    }
  },
  computed: {
    uiCtrl() {
      return this.$store.state.uiCtrl
    },
    organizations() {
      return this.$store.state.organization.organizations
    },
    authorityEmployeeSummary() {
      return this.$store.state.accessAuthority.authorityEmployeeSummary
    },
    flattenedRows() {
      const result = []
      const employees = this.authorityEmployeeSummary.employees || []

      employees.forEach((employee) => {
        const employeeCode = employee.employee_code
        const employeeName = `${employee.firstname_th} ${employee.lastname_th}`
        const organizationName = employee.organization?.title_th || ''

        const hardwares = employee.hardwares || []

        hardwares.forEach((hardware, hardwareIndex) => {
          const hardware_uuid = hardware.hardware_uuid
          const hardwareCode = hardware.hardware_code
          const hardwareTitle = hardware.title_th

          const authorities = hardware.authorities || []

          authorities.forEach((auth, authIndex) => {
            result.push({
              employeeCode,
              employeeName,
              organizationName,
              showEmployee: hardwareIndex === 0 && authIndex === 0,
              employeeRowSpan: hardwares.reduce(
                (total, e) => total + (e.authorities?.length || 1),
                0
              ),
              hardware_uuid,
              hardwareCode,
              hardwareTitle,
              showHardware: authIndex === 0,
              hardwareRowSpan: authorities.length || 1,
              authority: `${auth.authority.authority_code} - ${auth.authority.title_th}`,
              authority_uuid: auth.authority.authority_uuid,
              timezone: `${auth.timezone.timezone_code} - ${auth.timezone.title_th}`,
              accessControlStartTime: auth.authority.access_control_start_time
                ? moment
                    .utc(auth.authority.access_control_start_time)
                    .format('DD/MM/YYYY HH:mm')
                : '',
              accessControlEndTime: auth.authority.access_control_end_time
                ? moment
                    .utc(auth.authority.access_control_end_time)
                    .format('DD/MM/YYYY HH:mm')
                : '',
            })
          })
        })
      })

      return result
    },
  },
  watch: {
    async dialogCheckPermissions(value) {
      if (value) {
        await this.getAuthorityEmployeeSummary()
      }
    },
    optionsEmployee: {
      async handler() {
        await this.getAuthorityEmployeeSummary()
      },
      deep: true,
    },
  },
  async created() {
    try {
      const { company_uuid } = this.$store.state.authentication.company

      this.loadingFilterOrganization = true

      const payload = {
        company_uuid,
        params: {
          dpc: 'access-authority-summary-to-list-organization',
        },
      }

      await this.fetchOrganizations(payload)
    } catch (error) {
      const snackbar = {
        show: true,
        color: 'error',
        timeout: -1,
        message: error?.response?.data?.message || 'Something went wrong!',
      }
      this.$store.commit('SET_SNACKBAR', snackbar)
    } finally {
      this.loadingFilterOrganization = false
    }
  },
  methods: {
    ...mapActions({
      fetchAuthorityEmployeeSummary:
        'accessAuthority/fetchAuthorityEmployeeSummary',
      fetchOrganizations: 'organization/fetchOrganizations',
      deleteAuthorityDeviceMember:
        'accessAuthority/deleteAuthorityDeviceMember',
      fetchAccessAuthority: 'accessAuthority/fetchAccessAuthority',
    }),
    onDeleteDevice(item) {
      this.confirmDeleteDevice = true
      this.confirmDeleteDeviceData = {
        title: 'ยืนยันการลบ',
        text: `คุณต้องการลบ Device`,
        value: `${item.hardwareCode} - ${item.hardwareTitle}`,
        item: { data: { ...item } },
      }
    },
    cancelConfirmDeleteDevice() {
      this.confirmDeleteDevice = false
    },
    async submitConfirmDeleteDevice({ data }) {
      try {
        const { company_uuid } = this.$store.state.authentication.company

        this.confirmDeleteDeviceLoading = true

        await this.deleteAuthorityDeviceMember({
          company_uuid,
          authority_uuid: data.authority_uuid,
          body: {
            hardware_uuid: data.hardware_uuid,
          },
        })

        const accessAuthority = await this.fetchAccessAuthority({
          company_uuid,
          authority_uuid: data.authority_uuid,
        })

        this.$store.commit(
          'accessAuthority/EDIT_ACCESS_AUTHORITY',
          accessAuthority
        )

        this.optionsEmployee.page = 1
        this.getAuthorityEmployeeSummary()

        const snackbar = {
          show: true,
          color: 'success',
          timeout: 4000,
          message: 'ลบข้อมูลสำเร็จ',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.confirmDeleteDeviceLoading = false
        this.confirmDeleteDevice = false
      }
    },
    handleRefresh() {
      this.optionsEmployee.page = 1
      this.getAuthorityEmployeeSummary()
    },
    handleSearch() {
      this.loading = true
      this.getAuthorityEmployeeSummaryBySearch()
    },
    handleFilterOrganization() {
      this.optionsEmployee.page = 1
      this.getAuthorityEmployeeSummary()
    },
    getAuthorityEmployeeSummaryBySearch: _.debounce(function () {
      this.optionsEmployee.page = 1
      this.getAuthorityEmployeeSummary()
    }, 1000),
    async getAuthorityEmployeeSummary() {
      try {
        this.loading = true

        const { sortBy, sortDesc, page, itemsPerPage } = this.optionsEmployee

        let sort = ''
        const sortByKey = sortBy[0] || ''

        if (sortByKey === 'employeeCode') sort = 'employee_code'

        if (sortDesc.length > 0) {
          if (sortDesc[0]) {
            sort += `,desc`
          } else {
            sort += `,asc`
          }
        }

        const { company_uuid } = this.$store.state.authentication.company

        const payload = {
          company_uuid,
          params: {
            // dpc: '',
            search: this.search || undefined,
            organization_uuid: this.filterOrganization || undefined,
            sort,
            limit: `${itemsPerPage},${(page - 1) * itemsPerPage}`,
          },
        }

        await this.fetchAuthorityEmployeeSummary(payload)
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.with-borders >>> table,
.with-borders >>> th,
.with-borders >>> td {
  border: 1px solid #ccc;
  border-collapse: collapse;
}

.with-borders >>> th,
.with-borders >>> td {
  padding: 8px;
}
</style>
